/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";
import { SALES_GRAPH } from "../graphql";

export type SalesGraphType = "costumeBooking" | "preBooking";

export interface ISalesGraphMonth {
  month: string;
  total: number;
}

export interface ISalesGraphDay {
  day: string;
  total: number;
}

export interface ISalesGraphYear {
  year: string;
  presentationName?: string;
  yearlyTotal: number;
  monthly: ISalesGraphMonth[];
  daily: ISalesGraphDay[];
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface ISalesCostumeInventoryMethods extends IPluginMethodMap {
  getCostume(args?: { criteria?: { type?: SalesGraphType } }): Promise<any>;
}

export class SalesCostumeInventoryService implements IAgentPlugin {
  readonly methods: ISalesCostumeInventoryMethods;

  constructor() {
    this.methods = {
      getCostume: this.getCostumeProps.bind(this),
    };
  }

  async getCostumeProps(args: any = {}) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    const type: SalesGraphType =
      args?.criteria?.type || args?.type || "costumeBooking";

    if (!client) {
      throw new Error("GraphQL client 'graphql-gateway' is not available");
    }

    try {
      const response = await client.query({
        query: SALES_GRAPH,
        variables: { type },
        fetchPolicy: "network-only",
      });

      const years: ISalesGraphYear[] =
        response?.data?.salesGraph?.years || [];

      return {
        type,
        years,
        isEmpty: years.length === 0,
      };
    } catch (error: any) {
      console.error("salesGraph query failed", error);
      throw new Error(error?.message || "Unable to retrieve sales graph data");
    }
  }
}
